import React, { useState, useEffect } from 'react';
import { X, User, Mail, Building, Phone, Tag, MessageSquare, Send, Star, Clock, ShoppingCart, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactsAPI, integracoesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-toastify';

const ContactSidebar = ({ telefone, nomeContato, clienteId, onClose }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [contato, setContato] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novoComentario, setNovoComentario] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pedidos, setPedidos] = useState([]);
  const [pedidoAberto, setPedidoAberto] = useState(null);
  const [pedidoModal, setPedidoModal] = useState(null);
  const [expandCampanhas, setExpandCampanhas] = useState(false);

  useEffect(() => {
    if (!telefone || !clienteId) return;
    setLoading(true);
    contactsAPI.getByTelefone(clienteId, telefone)
      .then(r => setContato(r.data))
      .catch(() => setContato(null))
      .finally(() => setLoading(false));
    integracoesAPI.pedidos(telefone).then(r => setPedidos(r.data || [])).catch(() => {});
  }, [telefone, clienteId]);

  const enviarComentario = async () => {
    if (!novoComentario.trim() || !contato?.id) return;
    setEnviando(true);
    try {
      await contactsAPI.addComentario(contato.id, { comentario: novoComentario.trim() });
      setNovoComentario('');
      const r = await contactsAPI.getByTelefone(clienteId, telefone);
      setContato(r.data);
    } catch { toast.error('Erro ao adicionar comentário'); }
    finally { setEnviando(false); }
  };

  const card = darkMode ? 'bg-zinc-800' : 'bg-gray-50';
  const t1 = darkMode ? 'text-white' : 'text-gray-900';
  const t2 = darkMode ? 'text-zinc-400' : 'text-gray-500';
  const border = darkMode ? 'border-zinc-700' : 'border-gray-200';

  if (loading) return (
    <div className={`w-full fixed inset-0 z-40 md:relative md:inset-auto md:z-auto md:w-[420px] md:border-l ${border} ${darkMode ? 'bg-zinc-900' : 'bg-white'} flex items-center justify-center`}>
      <div className="animate-spin rounded-full h-6 w-6 border-2 border-nomoz-orange border-t-transparent" />
    </div>
  );

  if (!contato) return (
    <div className={`w-full fixed inset-0 z-40 md:relative md:inset-auto md:z-auto md:w-[420px] md:border-l ${border} ${darkMode ? 'bg-zinc-900' : 'bg-white'} p-4`}>
      <div className="flex justify-between items-center mb-4">
        <span className={`text-sm font-semibold ${t1}`}>Contato</span>
        <button onClick={onClose} className={`p-1 rounded ${darkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'}`}><X className="h-4 w-4" /></button>
      </div>
      <div className="flex flex-col items-center gap-3">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${darkMode ? 'bg-zinc-700 text-white' : 'bg-gray-200 text-gray-700'}`}>
          {(nomeContato || telefone || '?')[0].toUpperCase()}
        </div>
        <p className={`text-sm font-medium ${t1}`}>{nomeContato || 'Sem nome'}</p>
        <p className={`text-xs ${t2}`}>{telefone}</p>
        <p className={`text-xs ${t2}`}>Contato não cadastrado</p>
        <button
          onClick={async () => {
            try {
              await contactsAPI.create({ cliente_id: clienteId, nome: nomeContato || telefone, telefone, status: true });
              toast.success('Contato salvo!');
              setLoading(true);
              const res = await contactsAPI.getByTelefone(clienteId, telefone);
              setContato(res.data);
              setLoading(false);
            } catch (e) { toast.error('Erro ao salvar contato'); }
          }}
          className="mt-1 px-4 py-2 bg-nomoz-orange text-white text-sm rounded-lg hover:opacity-90 flex items-center gap-2"
        >
          <User className="h-4 w-4" /> Salvar Contato
        </button>
      </div>
    </div>
  );

  const stats = contato.estatisticas || {};

  return (
    <div className={`w-full fixed inset-0 z-40 md:relative md:inset-auto md:z-auto md:w-[420px] md:border-l ${border} ${darkMode ? 'bg-zinc-900' : 'bg-white'} overflow-y-auto`}>
      {/* Header */}
      <div className={`p-4 border-b ${border}`}>
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${darkMode ? 'bg-zinc-700 text-white' : 'bg-gray-200 text-gray-700'}`}>
              {(contato.nome || '?')[0].toUpperCase()}
            </div>
            <div>
              <p className={`text-sm font-semibold ${t1}`}>{contato.nome || 'Sem nome'}</p>
              <p className={`text-xs ${t2}`}>{contato.telefone}</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-1 rounded ${darkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-100'}`}><X className="h-4 w-4" /></button>
        </div>
        {contato.lead_score > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <Star className="h-3.5 w-3.5 text-amber-500" />
            <span className={`text-xs font-medium ${t2}`}>Lead Score: {contato.lead_score}</span>
          </div>
        )}
      </div>

      {/* Dados */}
      {(contato.email || contato.empresa || contato.cargo || contato.endereco) && (
      <div className={`p-4 border-b ${border} space-y-2`}>
        {contato.email && <div className="flex items-center gap-2"><Mail className={`h-3.5 w-3.5 ${t2}`} /><span className={`text-xs ${t1}`}>{contato.email}</span></div>}
        {contato.empresa && <div className="flex items-center gap-2"><Building className={`h-3.5 w-3.5 ${t2}`} /><span className={`text-xs ${t1}`}>{contato.empresa}{contato.cargo ? ` · ${contato.cargo}` : ''}</span></div>}
        {contato.endereco && <div className="flex items-center gap-2"><Phone className={`h-3.5 w-3.5 ${t2}`} /><span className={`text-xs ${t1}`}>{contato.endereco}</span></div>}
      </div>
      )}

      {/* Info rápida */}
      <div className={`p-4 border-b ${border} space-y-2`}>
        <div className="flex items-center justify-between">
          <span className={`text-xs ${t2}`}>Temperatura</span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            contato.lead_temperature === 'quente' ? 'bg-red-500/10 text-red-500' :
            contato.lead_temperature === 'morno' ? 'bg-amber-500/10 text-amber-500' :
            'bg-blue-500/10 text-blue-500'
          }`}>{contato.lead_temperature || 'frio'}</span>
        </div>
        {contato.ultima_interacao && (
          <div className="flex items-center justify-between">
            <span className={`text-xs ${t2}`}>Última interação</span>
            <span className={`text-xs ${t1}`}>{new Date(contato.ultima_interacao).toLocaleDateString('pt-BR')}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className={`text-xs ${t2}`}>Conversas</span>
          <span className={`text-xs ${t1}`}>{contato.total_conversas || 0}</span>
        </div>
      </div>

      {/* Stats rápidos */}
      <div className={`p-4 border-b ${border}`}>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className={`rounded-lg p-2 ${card}`}>
            <p className={`text-lg font-bold ${t1}`}>{stats.campanhas?.length || 0}</p>
            <p className={`text-[10px] ${t2}`}>Campanhas</p>
          </div>
          <div className={`rounded-lg p-2 ${card}`}>
            <p className={`text-lg font-bold ${t1}`}>{stats.atendimento?.total || 0}</p>
            <p className={`text-[10px] ${t2}`}>Mensagens</p>
          </div>
          <div className={`rounded-lg p-2 ${card}`}>
            <p className={`text-lg font-bold ${t1}`}>{stats.grupos?.length || 0}</p>
            <p className={`text-[10px] ${t2}`}>Grupos</p>
          </div>
        </div>
      </div>

      {/* Tags */}
      {stats.historicoTags?.length > 0 && (() => {
        const todas = stats.historicoTags;
        // Agrupar por conversa_id
        const porConversa = {};
        todas.forEach(t => {
          const key = t.conversa_id || 'sem_conversa';
          if (!porConversa[key]) porConversa[key] = { tags: new Map(), data: t.data_acao };
          porConversa[key].tags.set(t.tag_nome, t);
          if (t.data_acao > porConversa[key].data) porConversa[key].data = t.data_acao;
        });
        const conversas = Object.values(porConversa).sort((a, b) => new Date(b.data) - new Date(a.data));
        return (
          <div className={`p-4 border-b ${border}`}>
            <p className={`text-xs font-semibold mb-2 ${t2}`}>Tags por atendimento</p>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {conversas.map((conv, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`text-[10px] whitespace-nowrap ${t2}`}>
                    {conv.data ? new Date(conv.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {[...conv.tags.values()].map((t, j) => (
                      <span key={j} className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium text-white ${i > 0 ? 'opacity-50' : ''}`} style={{ backgroundColor: t.tag_cor }}>{t.tag_nome}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Campanhas */}
      {stats.campanhas?.length > 0 && (() => {
        const campanhasVisiveis = expandCampanhas ? stats.campanhas : stats.campanhas.slice(0, 2);
        return (
          <div className={`p-4 border-b ${border}`}>
            <p className={`text-xs font-semibold mb-2 ${t2}`}>📢 Campanhas ({stats.campanhas.length})</p>
            <div className="space-y-1.5">
              {campanhasVisiveis.map((c, i) => (
                <div key={i} className={`flex items-center justify-between rounded-lg px-2 py-1.5 ${card}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium truncate ${t1}`}>{c.nome}</p>
                    <p className={`text-[10px] ${t2}`}>
                      {c.mensagens_enviadas} msg • {c.status_execucao === 'completa' ? '✅' : c.status_execucao === 'executando' ? '🔄' : c.status_execucao === 'erro' ? '❌' : '📋'} {c.status_execucao || 'rascunho'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {stats.campanhas.length > 2 && (
              <button onClick={() => setExpandCampanhas(!expandCampanhas)} className={`text-[10px] mt-1.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'} hover:underline`}>
                {expandCampanhas ? '▲ Recolher' : `▼ Ver mais ${stats.campanhas.length - 2} campanhas`}
              </button>
            )}
          </div>
        );
      })()}

      {/* Pedidos */}
      {pedidos.length > 0 && (
        <div className={`p-4 border-b ${border}`}>
          <p className={`text-xs font-semibold mb-2 ${t2}`}><ShoppingCart size={12} className="inline mr-1" />Pedidos ({pedidos.length})
            <button onClick={() => navigate('/pedidos')} className="ml-2 text-blue-500 hover:underline inline-flex items-center gap-0.5"><ExternalLink size={10} />Ver todos</button>
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {pedidos.map((p, i) => (
              <div key={i} className={`rounded-lg p-2 cursor-pointer hover:opacity-80 ${card}`} onClick={() => window.open(`/pedidos?pedido=${p.id}`, '_blank')}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                      p.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' : p.status === 'preparando' ? 'bg-purple-100 text-purple-700' : p.status === 'saiu_entrega' ? 'bg-orange-100 text-orange-700' :
                      p.status === 'confirmado' ? 'bg-blue-100 text-blue-700' :
                      p.status === 'entregue' ? 'bg-green-100 text-green-700' :
                      p.status === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                    }`}>{({'pendente':'🔔 Novo','preparando':'📦 Preparando','saiu_entrega':'🚀 A caminho','entregue':'✅ Concluído','cancelado':'❌ Cancelado'})[p.status] || p.status}</span>
                    <span className={`text-xs font-bold ${t1}`}>R$ {parseFloat(p.total || 0).toFixed(2)}</span>
                  </div>
                  <span className={`text-[10px] ${t2}`}>{p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : ''}</span>
                </div>
                <p className={`text-[10px] mt-1 ${t2}`}>{(p.itens || []).length} itens</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Pedido */}
      {pedidoModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setPedidoModal(null)}>
          <div className={`rounded-xl p-6 w-96 max-h-[80vh] overflow-y-auto ${darkMode ? 'bg-zinc-800 text-white' : 'bg-white text-gray-900'}`} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">📋 Detalhes do Pedido</h3>
              <button onClick={() => setPedidoModal(null)} className="p-1"><X size={18} /></button>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <span className={`text-sm px-2 py-1 rounded font-medium ${
                pedidoModal.status === 'pendente' ? 'bg-yellow-100 text-yellow-700' :
                pedidoModal.status === 'confirmado' ? 'bg-blue-100 text-blue-700' :
                pedidoModal.status === 'entregue' ? 'bg-green-100 text-green-700' :
                pedidoModal.status === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
              }`}>{({'pendente':'⏳ Pendente','confirmado':'✅ Confirmado','preparando':'📦 Preparando','saiu_entrega':'🛵 Saiu p/ entrega','entregue':'✔️ Entregue','cancelado':'❌ Cancelado'})[pedidoModal.status] || pedidoModal.status}</span>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {pedidoModal.created_at ? new Date(pedidoModal.created_at).toLocaleString('pt-BR') : ''}
              </span>
            </div>
            <div className={`rounded-lg p-3 mb-4 ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
              <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Itens</p>
              {(pedidoModal.itens || []).map((item, j) => (
                <div key={j} className="flex justify-between text-sm mb-1">
                  <span>{item.qtd}x {item.nome}</span>
                  <span className="font-medium">R$ {(parseFloat(item.preco || 0) * item.qtd).toFixed(2)}</span>
                </div>
              ))}
              <div className={`border-t mt-2 pt-2 flex justify-between text-sm font-bold ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
                <span>Total</span>
                <span>R$ {parseFloat(pedidoModal.total || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                {pedidoModal.delivery ? <span>🛵 Entrega</span> : <span>🏪 Retirada na loja</span>}
              </div>
              {pedidoModal.endereco && <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>📍 {pedidoModal.endereco}</p>}
              {pedidoModal.pagamento && <p>💳 {pedidoModal.pagamento}</p>}
            </div>
            <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-zinc-600' : 'border-gray-200'}`}>
              <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Alterar status</p>
              <div className="flex gap-2 flex-wrap">
                {['pendente', 'preparando', 'saiu_entrega', 'entregue', 'cancelado'].map(s => {
                  const labels = { pendente: '🔔 Novo', preparando: '📦 Preparando', saiu_entrega: '🚀 A caminho', entregue: '✅ Concluído', cancelado: '❌ Cancelado' };
                  const colors = { pendente: 'bg-yellow-100 text-yellow-700', preparando: 'bg-blue-100 text-blue-700', saiu_entrega: 'bg-orange-100 text-orange-700', entregue: 'bg-green-100 text-green-700', cancelado: 'bg-red-100 text-red-700' };
                  return (
                    <button key={s} disabled={pedidoModal.status === s}
                      onClick={async () => {
                        try {
                          await integracoesAPI.atualizarStatusPedido(pedidoModal.id, s);
                          setPedidoModal({ ...pedidoModal, status: s });
                          setPedidos(prev => prev.map(p => p.id === pedidoModal.id ? { ...p, status: s } : p));
                          toast.success(`Status: ${labels[s]}`);
                        } catch { toast.error('Erro ao atualizar'); }
                      }}
                      className={`px-3 py-1.5 text-xs rounded font-medium disabled:opacity-30 ${colors[s]}`}>{labels[s]}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comentários */}
      <div className="p-4">
        <p className={`text-xs font-semibold mb-2 ${t2}`}>Comentários internos</p>
        <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
          {(contato.comentarios || []).map((c, i) => (
            <div key={i} className={`rounded-lg p-2 ${card}`}>
              <p className={`text-xs ${t1}`}>{c.comentario}</p>
              <p className={`text-[10px] mt-1 ${t2}`}>{c.usuario_nome} · {new Date(c.create_at).toLocaleDateString('pt-BR')}</p>
            </div>
          ))}
          {(!contato.comentarios || contato.comentarios.length === 0) && (
            <p className={`text-xs text-center py-2 ${t2}`}>Nenhum comentário</p>
          )}
        </div>
        <div className="flex gap-2">
          <input type="text" value={novoComentario} onChange={e => setNovoComentario(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && enviarComentario()}
            placeholder="Adicionar nota..."
            className={`flex-1 text-xs rounded-lg px-3 py-2 border ${darkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'border-gray-300'}`} />
          <button onClick={enviarComentario} disabled={enviando || !novoComentario.trim()}
            className="px-3 py-2 bg-nomoz-orange text-white rounded-lg disabled:opacity-50">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactSidebar;
